'use client';

import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
// @ts-ignore Vite resolves ?url worker bundles
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

if (typeof window !== 'undefined') {
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;
}

type InlinePdfViewerProps = {
  url: string;
  className?: string;
  /** Max width in CSS px used to pick render scale (pages stay sharp on retina via devicePixelRatio) */
  maxRenderWidth?: number;
};

/**
 * Renders a PDF into the page with pdf.js (no browser plugin / print chrome).
 * Uses a signed HTTPS URL; relies on storage CORS allowing the app origin.
 */
export function InlinePdfViewer({ url, className, maxRenderWidth = 880 }: InlinePdfViewerProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!url) return;

    let cancelled = false;
    let loadingTask: ReturnType<typeof pdfjsLib.getDocument> | null = null;
    const hostAtMount = hostRef.current;

    const run = async () => {
      const host = hostRef.current;
      if (!host) return;
      host.innerHTML = '';
      setPhase('loading');
      setErrorMessage('');

      try {
        await new Promise<void>((r) => requestAnimationFrame(() => r()));
        await new Promise<void>((r) => requestAnimationFrame(() => r()));
        if (cancelled) return;

        loadingTask = pdfjsLib.getDocument({ url, withCredentials: false });
        const pdf = await loadingTask.promise;
        if (cancelled) return;

        const containerW = Math.min(maxRenderWidth, Math.max(320, host.offsetWidth || maxRenderWidth));
        const dpr = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 1;

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          if (cancelled) break;
          const page = await pdf.getPage(pageNum);
          const base = page.getViewport({ scale: 1 });
          const scale = (containerW / base.width) * dpr;
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement('canvas');
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.style.width = `${Math.floor(viewport.width / dpr)}px`;
          canvas.style.height = `${Math.floor(viewport.height / dpr)}px`;
          canvas.className =
            'mx-auto mb-4 block max-w-full rounded-md border border-border bg-white shadow-sm dark:bg-zinc-950';

          const ctx = canvas.getContext('2d');
          if (!ctx) continue;

          await page.render({ canvasContext: ctx, viewport }).promise;
          host.appendChild(canvas);
        }

        if (!cancelled) setPhase('ready');
      } catch (e: unknown) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : 'Failed to render PDF';
        setErrorMessage(msg);
        setPhase('error');
      }
    };

    void run();

    return () => {
      cancelled = true;
      try {
        loadingTask?.destroy?.();
      } catch {
        // OBS-11 a-ignore: teardown on unmount; the loading task may already be finished or destroyed.
      }
      if (hostAtMount) hostAtMount.innerHTML = '';
    };
  }, [url, maxRenderWidth]);

  return (
    <div className={className}>
      {phase === 'loading' && (
        <p className="text-sm text-muted-foreground mb-3" aria-live="polite">
          Loading PDF preview…
        </p>
      )}
      {phase === 'error' && (
        <p className="text-sm text-destructive mb-3" role="alert">
          {errorMessage}
        </p>
      )}
      <div ref={hostRef} className="min-h-[80px]" />
    </div>
  );
}
